import { useEffect } from "react";

/**
 * Reveals every `[data-reveal]` element inside `root` once it scrolls into view,
 * by adding the `is-revealed` class. One observer for the whole page, each
 * element unobserved after its first reveal so nothing re-animates on scroll-up.
 *
 * `data-reveal-delay="1"` on an element staggers it by one step (~70ms).
 */
export function useScrollReveal(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = root.current;
    if (!container) return;

    const nodes = Array.from(container.querySelectorAll<HTMLElement>("[data-reveal]"));
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduced || typeof IntersectionObserver === "undefined") {
      nodes.forEach((node) => node.classList.add("is-revealed"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [root]);
}
